import { useMemo, type FC } from 'react';

import { Text } from '@/core/tamagoshi/Text/Text';
import { CouponDescription } from '@/domains/loyalty/components/Coupon/components/CouponDescription/CouponDescription';
import { CouponLayout } from '@/domains/loyalty/components/Coupon/components/CouponLayout/CouponLayout';
import { CouponContext } from '@/domains/loyalty/components/Coupon/CouponContext';

import { CodeBlock } from './components/CodeBlock/CodeBlock';
import { bulkPurchaseCouponLabel, couponCode } from './translations';

import styles from './Coupon.module.scss';

interface RightContentProps {
  code: string;
  context: string;
  isBulkPurchaseCoupon?: boolean;
}

const RightContent: FC<RightContentProps> = ({
  code,
  context,
  isBulkPurchaseCoupon,
}) => (
  <>
    <Text className={styles.textBlock} tag="span" type="tiny">
      {isBulkPurchaseCoupon ? bulkPurchaseCouponLabel : couponCode}
    </Text>
    <CodeBlock couponCode={code} context={context} />
  </>
);

export interface ProductCouponProps {
  amount?: number;
  code: string;
  percent?: number;
  context: string;
  isBulkPurchaseCoupon?: boolean;
  minPurchase?: number;
  validUntil?: string;
}

export const Coupon: FC<ProductCouponProps> = ({
  code,
  percent,
  amount,
  context,
  minPurchase,
  validUntil,
}) => {
  const contextProviderData = useMemo(
    () => ({ amount, code, context, minPurchase, percent, validUntil }),
    [amount, code, context, minPurchase, percent, validUntil],
  );
  return (
    <CouponContext.Provider value={contextProviderData}>
      <CouponLayout
        rightContent={<RightContent code={code} context={context} />}
        description={[<CouponDescription />]}
      />
    </CouponContext.Provider>
  );
};

Coupon.displayName = 'Coupon';
